import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingBag, CreditCard, MapPin, ChevronRight, Package, Truck, CheckCircle, Clock, Loader, AlertCircle, ClipboardList, User, ArrowLeft, XCircle, RefreshCw, Receipt } from 'lucide-react';
import axios from 'axios';
import SpiceLevel from '../SpiceLevel';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import './index.css';
import './premium-details.css';
import '../ProfileSectionPage/index.css';
import { useNavigate, useParams } from 'react-router-dom';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const ORDERS_PAGE_LIMIT = 10;

export const MyOrdersModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { orderId: urlOrderId } = useParams();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // Cancel order state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null); // { type: 'success'|'error', text: '' }

  const [activeFilters, setActiveFilters] = useState({
    orderType: '',
    status: '',
    orderSerialNo: '',
    fromDate: '',
    toDate: ''
  });
  const [tempFilters, setTempFilters] = useState({
    orderType: '',
    status: '',
    orderSerialNo: '',
    fromDate: '',
    toDate: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSelectedOrder(null);
      setOrderDetails(null);
      setCancelMessage(null);
      fetchOrders(1);
    }
  }, [isOpen, activeFilters, perPage]);

  // Handle URL-based order selection and clearing
  useEffect(() => {
    if (orders.length > 0) {
      if (urlOrderId) {
        // If we have an ID in URL, find it and select it
        // FIXED: Using slice(-5) to match from the end for uniqueness (avoiding same-prefix collisions)
        const targetOrder = orders.find(o => String(o.id).slice(-5) === urlOrderId);
        if (targetOrder && (!selectedOrder || String(selectedOrder.id) !== String(targetOrder.id))) {
          setSelectedOrder(targetOrder);
          setOrderDetails(null);
          fetchOrderDetails(targetOrder);
        }
      } else if (selectedOrder) {
        // If no ID in URL but we have a selected order, clear it
        setSelectedOrder(null);
        setOrderDetails(null);
      }
    }
  }, [orders, urlOrderId, selectedOrder]);

  const fetchOrders = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError(null);

    try {
      const token = getValidAuthSession()?.token;

      if (!token) {
        setError('Please login to view your orders');
        setLoading(false);
        return;
      }

      const params = {
        paginate: 1,
        page: currentPage,
        per_page: perPage,
        order_column: 'id',
        order_by: 'desc',
        excepts: 'undefined'
      };

      if (activeFilters.orderType) params.order_type = activeFilters.orderType;
      if (activeFilters.status) params.status = activeFilters.status;
      if (activeFilters.orderSerialNo) params.order_serial_no = activeFilters.orderSerialNo;
      if (activeFilters.fromDate) params.from_date = new Date(activeFilters.fromDate).toISOString();
      if (activeFilters.toDate) params.to_date = new Date(activeFilters.toDate).toISOString();

      const response = await axios.get(`${API_BASE_URL}/web/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        params
      });

      if (response.data && response.data.data) {
        // Filter out abandoned/unpaid online orders to avoid confusion
        // Typically: payment_method != 1 (Cash) AND payment_status == 10 (Unpaid) or 15 (Failed)
        const validOrders = response.data.data.filter(order => {
          const isCash = Number(order.payment_method) === 1 || String(order.payment_method).toLowerCase().includes('cash');
          const isUnpaidOrFailed = Number(order.payment_status) === 10 || Number(order.payment_status) === 15;

          if (!isCash && isUnpaidOrFailed) {
            return false; // Hide abandoned online checkouts
          }
          return true;
        });

        const formattedOrders = validOrders.map(order => ({
          id: order.order_serial_no,
          orderId: order.id,
          date: order.order_datetime,
          itemCount: order.order_items,
          total: order.total_currency_price || `₹${order.total || '0.00'}`,
          status: getStatusText(order.status, order.status_name),
          statusCode: Number(order.status),
          deliveryAddress: order.branch?.address || 'N/A',
          branchName: order.branch?.name || 'N/A',
          city: order.branch?.city || '',
          state: order.branch?.state || '',
          zipCode: order.branch?.zip_code || '',
          paymentMethod: getPaymentMethodText(order.payment_method),
          paymentStatus: order.payment_status,
          orderType: order.order_type_text,
          token: order.token,
          subtotal: order.subtotal_currency_price || `₹${order.subtotal || '0.00'}`,
          discount: order.discount_currency_price || `₹${order.discount || '0.00'}`,
          tax: order.total_tax_currency_price || `₹${order.total_tax || '0.00'}`,
          deliveryCharge: order.delivery_charge_currency_price || `₹${order.delivery_charge || '0.00'}`,
          preparationTime: order.preparation_time,
          customerName: order.customer?.name || order.user?.name || 'Guest User',
          customerEmail: order.customer?.email || order.user?.email || 'Not provided',
          customerPhone: order.customer?.phone || order.user?.phone || 'Not provided'
        }));

        setOrders(formattedOrders);
        setTotalCount(response.data.total || response.data.meta?.total || 0);
        setLastPage(response.data.last_page || response.data.meta?.last_page || 1);
        setPage(currentPage);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [activeFilters, perPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      fetchOrders(newPage);
    }
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setActiveFilters({ ...tempFilters });
    setPage(1);
  };

  const clearFilters = () => {
    const emptyFilters = {
      orderType: '',
      status: '',
      orderSerialNo: '',
      fromDate: '',
      toDate: ''
    };
    setTempFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setPage(1);
  };

  const fetchOrderDetails = async (orderRef) => {
    const orderId = typeof orderRef === 'object' ? orderRef.orderId : orderRef;
    setLoadingDetails(true);

    try {
      const token = getValidAuthSession()?.token;

      // Clear cancellation UI state when switching orders
      setCancelMessage(null);
      setShowCancelConfirm(false);
      setCancelReason('');

      if (!token) {
        setError('Please login to view order details');
        setLoadingDetails(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/web/order/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data && response.data.data) {
        const details = response.data.data;
        setOrderDetails({
          orderNumber: details.order_serial_no,
          orderId: details.id,
          status: getStatusText(details.status, details.status_name),
          date: details.order_date,
          time: details.order_time,
          orderType: details.order_type_text,
          branch: details.branch?.name || 'N/A',
          deliveryDate: details.delivery_date,
          deliveryTime: details.delivery_time,
          token: details.token,
          preparationTime: details.preparation_time,
          paymentMethod: getPaymentMethodText(details.payment_method),
          paymentStatus: getPaymentStatusText(details.payment_status),

          // User information
          userName: details.customer?.name || details.user?.name || details.customer_name || (typeof orderRef === 'object' ? orderRef.customerName : null) || 'Guest User',
          userEmail: details.customer?.email || details.user?.email || details.customer_email || (typeof orderRef === 'object' ? orderRef.customerEmail : null) || 'Not provided',
          userPhone: details.customer?.phone || details.user?.phone || details.customer_phone || (typeof orderRef === 'object' ? orderRef.customerPhone : null) || 'Not provided',

          // Table information
          tableName: details.table_name || 'N/A',

          deliveryAddress: details.order_address ? {
            label: details.order_address.label || '',
            address: details.order_address.address || '',
            apartment: details.order_address.apartment || '',
            city: details.order_address.city || '',
            state: details.order_address.state || '',
            zip_code: details.order_address.zip_code || details.order_address.post_code || '',
            landmark: details.order_address.landmark || '',
            recipient_name: details.order_address.recipient_name || '',
            phone: details.order_address.phone || ''
          } : null,

          items: details.order_items?.map(item => ({
            id: item.id,
            name: item.item_name,
            shortCode: item.short_code,
            spiceLevel: item.spice_level || 0, // 0-5; 0 shows nothing
            image: item.item_image,
            quantity: parseFloat(item.quantity),
            price: item.price,
            total: item.total_currency_price,
            variations: item.item_variations || [],
            extras: item.item_extras || [],
            comboGroups: item.combo_groups || [],
            comboComponents: item.combo_components || [],
            addons: item.item_addons || [],
            weightInfo: item.weight_info || null,
            // Raw per-unit weight (kg) — sent alongside weight_info at order
            // creation (buildOrderItemsList's orderItemPayload.weight), same
            // top-level field name. Lets us compute a simple "250g × 2" label
            // instead of parsing the pre-formatted weight_info string.
            weight: item.weight != null ? Number(item.weight) : null,
            instruction: item.instruction,
            kitchenName: item.kitchen_name,
            isFreeItem: item.is_free_item,
            kotNo: item.kot_no
          })) || [],

          // Pricing breakdown from the correct API fields
          subtotal: details.pricing_breakdown?.item_subtotal_formatted || details.subtotal_currency_price || `₹${details.subtotal || '0.00'}`,
          discount: details.pricing_breakdown?.total_discounts_formatted || details.discount_currency_price || `₹${details.discount || '0.00'}`,
          tax: details.pricing_breakdown?.tax_amount_formatted || details.total_tax_currency_price || `₹${details.total_tax || '0.00'}`,
          deliveryCharge: details.pricing_breakdown?.delivery_charge_formatted || details.delivery_charge_currency_price || `₹${details.delivery_charge || '0.00'}`,
          packagingCharge: details.pricing_breakdown?.packaging_charge_formatted || details.packaging_charge_currency_price || '₹0.00',
          total: details.pricing_breakdown?.final_total_formatted || details.total_currency_price || `₹${details.total || '0.00'}`,

          // Branch details
          branchAddress: details.branch?.address || '',
          branchCity: details.branch?.city || '',
          branchState: details.branch?.state || '',
          branchZip: details.branch?.zip_code || '',
          branchPhone: details.branch?.phone || '',

          // Offer information
          offerSummary: details.offer_summary,
          primaryOffer: details.primary_offer,
          freeItems: details.free_items || [],

          // Loyalty points
          loyalty: details.loyalty,

          // Statistics
          orderStatistics: details.order_statistics,

          // Transaction details
          transaction: details.transaction ? {
            id: details.transaction.id,
            transactionNo: details.transaction.transaction_no,
            amount: details.transaction.amount,
            paymentMethod: details.transaction.payment_method,
            type: details.transaction.type,
            date: details.transaction.date
          } : null,

          // Source info
          source: details.source_text || 'Unknown',
          statusCode: Number(details.status),
          reason: details.reason || details.cancellation_reason,
          taxBreakdown: details.tax_breakdown || []
        });
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(err.response?.data?.message || 'Failed to load order details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectOrder = async (order) => {
    // FIXED: Using slice(-5) to take the last 5 digits of the serial number for routing
    const idShort = String(order.id).slice(-5);
    navigate(`/orders/${idShort}`);
  };

  // Same "under 1kg shows grams" convention already used by the weight
  // picker (WeightSelectionModal's formatWeightLabel) — kept local here
  // since it's the only other place that needs it.
  const formatWeightLabel = (weightKg) => {
    const w = Number(weightKg);
    if (!Number.isFinite(w) || w <= 0) return null;
    return w < 1 ? `${Math.round(w * 1000)}g` : `${w}kg`;
  };

  const getStatusText = (statusCode, statusName) => {
    const statusMap = {
      1: 'Pending',
      4: 'Accepted',
      7: 'Processing',
      10: 'Out for Delivery',
      13: 'Delivered',
      16: 'Cancelled',
      19: 'Rejected',
      22: 'Returned',
      25: 'Prepared',
      28: 'Out of Stock',
      31: 'Ready',
      34: 'Draft',
      37: 'Rider Assigned'
    };
    return statusMap[statusCode] || statusName || 'Unknown';
  };

  const getPaymentMethodText = (method) => {
    if (!method) return 'Unknown';

    // Handle string values from API
    const methodLower = String(method).toLowerCase();
    if (methodLower.includes('cash')) return 'Cash';
    if (methodLower.includes('card')) return 'Card';
    if (methodLower.includes('upi')) return 'UPI';
    if (methodLower.includes('online')) return 'Online';

    const methodMap = {
      1: 'Cash',
      2: 'Card',
      3: 'UPI',
      4: 'Online',
      5: 'Online'
    };
    return methodMap[method] || String(method);
  };

  const getPaymentStatusText = (status) => {
    const statusMap = {
      5: 'Paid',
      10: 'Unpaid',
      15: 'Failed',
      20: 'Refunded'
    };
    return statusMap[status] || 'Unknown';
  };

  const handleClose = () => {
    navigate('/orders');
    onClose();
  };

  const handleManualRefresh = () => {
    if (selectedOrder) {
      fetchOrderDetails(selectedOrder);
    } else {
      fetchOrders(page);
    }
  };

  // if (!isOpen) return null;

  const getStatusIcon = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower.includes('deliver') || statusLower.includes('complete')) return <CheckCircle size={20} />;
    if (statusLower.includes('confirm') || statusLower.includes('progress') || statusLower.includes('process') || statusLower.includes('prepar') || statusLower.includes('ready')) return <Truck size={20} />;
    if (statusLower.includes('cancel') || statusLower.includes('reject')) return <X size={20} />;
    if (statusLower.includes('rider')) return <Truck size={20} />;
    if (statusLower.includes('pending')) return <Clock size={20} />;
    return <Clock size={20} />;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="profile-section-header">
        <div className="profile-section-header-inner">
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', padding: 0, marginRight: '15px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--color-primary, #ff6b35)' }}
            title="Go Back"
          >
            <ArrowLeft size={28} />
          </button>
          <ShoppingBag size={28} />
          <h2>My Orders</h2>
          <button
            className="orders-refresh-button"
            onClick={handleManualRefresh}
            disabled={loading || loadingDetails}
            title="Refresh Orders"
          >
            <RefreshCw size={20} className={(loading || loadingDetails) ? 'spin' : ''} />
          </button>
        </div>
      </div>

      <div className="profile-section-content">
        {!selectedOrder ? (
          <>
            {/* Filters Section */}
            <div className="orders-filters-bar">
              <div className="filters-main-row">
                <div className="filter-item">
                  <select
                    value={activeFilters.orderType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTempFilters(prev => ({ ...prev, orderType: val }));
                      setActiveFilters(prev => ({ ...prev, orderType: val }));
                      setPage(1);
                    }}
                    className="filter-select"
                  >
                    <option value="">Order Type</option>
                    <option value="5">Delivery</option>
                    <option value="10">Takeaway</option>
                  </select>
                </div>

                <div className="filter-item">
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(parseInt(e.target.value));
                      setPage(1);
                    }}
                    className="filter-select per-page-select"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                <button
                  className={`filter-toggle-btn ${isFilterOpen ? 'active' : ''}`}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <AlertCircle size={16} />
                  <span>Filter</span>
                  <ChevronRight size={14} className={isFilterOpen ? 'rotate-down' : ''} />
                </button>
              </div>

              {isFilterOpen && (
                <div className="filters-expanded-panel">
                  <div className="filters-grid">
                    <div className="filter-field">
                      <label>Order ID</label>
                      <input
                        type="text"
                        placeholder="Enter Order ID"
                        value={tempFilters.orderSerialNo}
                        onChange={(e) => handleTempFilterChange('orderSerialNo', e.target.value)}
                      />
                    </div>

                    <div className="filter-field">
                      <label>Status</label>
                      <select
                        value={tempFilters.status}
                        onChange={(e) => handleTempFilterChange('status', e.target.value)}
                      >
                        <option value="">-- All Status --</option>
                        <option value="1">Pending</option>
                        <option value="4">Accepted</option>
                        <option value="7">Processing</option>
                        <option value="10">Out for Delivery</option>
                        <option value="13">Delivered</option>
                        <option value="16">Cancelled</option>
                        <option value="19">Rejected</option>
                        <option value="22">Returned</option>
                        <option value="31">Ready</option>
                      </select>
                    </div>

                    <div className="filter-field">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={tempFilters.fromDate}
                        onChange={(e) => handleTempFilterChange('fromDate', e.target.value)}
                      />
                    </div>

                    <div className="filter-field">
                      <label>To Date</label>
                      <input
                        type="date"
                        value={tempFilters.toDate}
                        onChange={(e) => handleTempFilterChange('toDate', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="filters-actions">
                    <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
                    <button className="apply-filters-btn" onClick={applyFilters}>Search</button>
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div className="orders-state-container loading">
                <Loader size={40} className="orders-spinner" />
                <p>Loading your orders...</p>
              </div>
            ) : error ? (
              <div className="orders-state-container error">
                <AlertCircle size={48} />
                <p>{error}</p>
                <button
                  onClick={fetchOrders}
                  className="orders-retry-btn"
                >
                  Try Again
                </button>
              </div>
            ) : orders.length === 0 ? (
              <div className="orders-state-container empty">
                <Package size={64} className="empty-icon" />
                <h3 className="empty-title">
                  {Object.values(activeFilters).some(v => v !== '') ? 'No Orders Found' : 'No Orders Yet'}
                </h3>
                <p className="empty-text">
                  {Object.values(activeFilters).some(v => v !== '') ? 'Try adjusting your filters' : 'Your order history will appear here'}
                </p>
                {Object.values(activeFilters).some(v => v !== '') && (
                  <button onClick={clearFilters} className="clear-filters-btn secondary">
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Table view for large and medium devices */}
                <div className="orders-table-container">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th className="centered-column">Type</th>
                        <th className="centered-column">Items</th>
                        <th className="centered-column">Total</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id}>
                          <td>
                            <div className="order-id-cell">
                              <Package size={16} />
                              <strong>{order.id}</strong>
                            </div>
                          </td>
                          <td>{order.date}</td>
                          <td className="centered-column">
                            <span className={`order-type-badge ${order.orderType?.toLowerCase()}`}>
                              {order.orderType}
                            </span>
                          </td>
                          <td className="centered-column">{order.itemCount} </td>
                          <td className="total-cell centered-column">{order.total}</td>
                          <td>
                            <div className="order-status" data-status={order.status.toLowerCase().replace(/\s+/g, '-')}>
                              {getStatusIcon(order.status)}
                              <span>{order.status}</span>
                            </div>
                          </td>
                          <td>
                            <button className="view-details-btn" onClick={() => handleSelectOrder(order)}>
                              View Details
                              <ChevronRight size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile view cards - Essential for mobile devices */}
                <div className="orders-list">
                  {orders.map(order => (
                    <div key={order.id} className="order-card" onClick={() => handleSelectOrder(order)}>
                      <div className="order-card-header">
                        <div className="order-id-section">
                          <Package size={18} />
                          <span>#{order.id}</span>
                        </div>
                        <div className="order-status" data-status={order.status.toLowerCase().replace(/\s+/g, '-')}>
                          {getStatusIcon(order.status)}
                          <span>{order.status}</span>
                        </div>
                      </div>
                      <div className="order-card-body">
                        <div className="order-info-row">
                          <span className="order-label">Date</span>
                          <span className="order-value">{order.date}</span>
                        </div>
                        <div className="order-info-row">
                          <span className="order-label">Order Type</span>
                          <span className={`order-type-badge ${order.orderType?.toLowerCase()}`}>
                            {order.orderType}
                          </span>
                        </div>
                        <div className="order-info-row">
                          <span className="order-label">Items</span>
                          <span className="order-value">{order.itemCount} item(s)</span>
                        </div>
                        <div className="order-info-row">
                          <span className="order-label">Total Amount</span>
                          <span className="order-value-bold">{order.total}</span>
                        </div>
                      </div>
                      <div className="order-card-footer">
                        <span>View Details</span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Component */}
                <div className="custom-pagination">
                  <button
                    className="pagination-arrow"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    <ChevronRight size={18} className="rotate-180" />
                  </button>

                  <div className="pagination-pages">
                    {[...Array(lastPage)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === lastPage ||
                        (p >= page - 1 && p <= page + 1)
                      ) {
                        return (
                          <button
                            key={p}
                            className={`page-number ${page === p ? 'active' : ''}`}
                            onClick={() => handlePageChange(p)}
                          >
                            {p}
                          </button>
                        );
                      } else if (p === page - 2 || p === page + 2) {
                        return <span key={p} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    className="pagination-arrow"
                    disabled={page === lastPage}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="order-details">
            <div className="order-details-top-actions">
              <button className="back-button" onClick={() => navigate(-1)}>
                <ChevronRight size={18} className="back-button-icon" />
                <span>Back to Orders</span>
              </button>
            </div>

            {loadingDetails ? (
              <div className="order-details-loader">
                <Loader size={40} className="orders-spinner" />
                <p>Fetching order details...</p>
              </div>
            ) : orderDetails ? (
              <div className="order-details-premium-container">
                <div className="order-details-card-main">
                  <div className="order-details-header-premium">
                    <div className="header-left">
                      <div className="order-icon-badge">
                        <ShoppingBag size={24} />
                      </div>
                      <div>
                        <h3>Order #{orderDetails.orderNumber}</h3>
                        <p className="order-timestamp">{orderDetails.date} at {orderDetails.time}</p>
                      </div>
                    </div>
                    <div className="order-status-premium" data-status={orderDetails.status.toLowerCase().replace(/\s+/g, '-')}>
                      {getStatusIcon(orderDetails.status)}
                      <span>{orderDetails.status}</span>
                    </div>
                  </div>

                  <div className="order-details-grid">
                    {/* Order Info Section */}
                    <div className="details-card-section">
                      <div className="section-title">
                        <ClipboardList size={18} />
                        <h4>Order Summary</h4>
                      </div>
                      <div className="section-body">
                        <div className="info-item">
                          <span className="label">Order Type</span>
                          <span className="value">{orderDetails.orderType}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Branch</span>
                          <span className="value">{orderDetails.branch}</span>
                        </div>
                        {orderDetails.deliveryDate && (
                          <div className="info-item">
                            <span className="label">{orderDetails.orderType === 'Delivery' ? 'Delivery Date' : 'Collection Date'}</span>
                            <span className="value">{orderDetails.deliveryDate}</span>
                          </div>
                        )}
                        {orderDetails.deliveryTime && (
                          <div className="info-item">
                            <span className="label">{orderDetails.orderType === 'Delivery' ? 'Delivery Time' : 'Collection Time'}</span>
                            <span className="value">{orderDetails.deliveryTime}</span>
                          </div>
                        )}
                        {orderDetails.tableName && orderDetails.tableName !== 'N/A' && (
                          <div className="info-item">
                            <span className="label">Table</span>
                            <span className="value">{orderDetails.tableName}</span>
                          </div>
                        )}
                        {orderDetails.token && (
                          <div className="info-item">
                            <span className="label">Token Number</span>
                            <span className="value token-highlight">{orderDetails.token}</span>
                          </div>
                        )}
                        {orderDetails.reason && (
                          <div className="info-item">
                            <span className="label">Reason</span>
                            <span className="value cancel-reason-text">{orderDetails.reason}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Customer Info Section */}
                    <div className="details-card-section">
                      <div className="section-title">
                        <User size={18} />
                        <h4>Customer Details</h4>
                      </div>
                      <div className="section-body">
                        <div className="info-item">
                          <span className="label">Name</span>
                          <span className="value">{orderDetails.userName}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Email</span>
                          <span className="value">{orderDetails.userEmail}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Phone</span>
                          <span className="value">{orderDetails.userPhone}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="order-details-grid">
                    {/* Destination Section */}
                    <div className="details-card-section">
                      <div className="section-title">
                        <MapPin size={18} />
                        <h4>{orderDetails.orderType === 'Delivery' ? 'Delivery Location' : 'Collection Point'}</h4>
                      </div>
                      <div className="section-body">
                        {orderDetails.orderType === 'Delivery' && orderDetails.deliveryAddress ? (
                          <>
                            {orderDetails.deliveryAddress.label && (
                              <div className="info-item">
                                <span className="label">Place Label</span>
                                <span className="value">
                                  <span className="place-label-tag">
                                    {orderDetails.deliveryAddress.label}
                                  </span>
                                </span>
                              </div>
                            )}
                            {orderDetails.deliveryAddress.recipient_name && (
                              <div className="info-item">
                                <span className="label">Deliver To</span>
                                <span className="value">{orderDetails.deliveryAddress.recipient_name}</span>
                              </div>
                            )}
                            <div className="info-item">
                              <span className="label">Address</span>
                              <span className="value">{orderDetails.deliveryAddress.address}</span>
                            </div>
                            {orderDetails.deliveryAddress.apartment && (
                              <div className="info-item">
                                <span className="label">Apt/Flat</span>
                                <span className="value">{orderDetails.deliveryAddress.apartment}</span>
                              </div>
                            )}
                            {orderDetails.deliveryAddress.landmark && (
                              <div className="info-item">
                                <span className="label">Landmark</span>
                                <span className="value">{orderDetails.deliveryAddress.landmark}</span>
                              </div>
                            )}
                            <div className="info-item">
                              <span className="label">Location</span>
                              <span className="value">
                                {[
                                  orderDetails.deliveryAddress.city,
                                  orderDetails.deliveryAddress.state,
                                  orderDetails.deliveryAddress.zip_code
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                            {orderDetails.deliveryAddress.phone && (
                              <div className="info-item">
                                <span className="label">Phone</span>
                                <span className="value">{orderDetails.deliveryAddress.phone}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="info-item">
                              <span className="label">Branch</span>
                              <span className="value">{orderDetails.branch}</span>
                            </div>
                            <div className="info-item">
                              <span className="label">Address</span>
                              <span className="value">{orderDetails.branchAddress}</span>
                            </div>
                            {orderDetails.branchPhone && (
                              <div className="info-item">
                                <span className="label">Phone</span>
                                <span className="value">{orderDetails.branchPhone}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Payment Info Section */}
                    <div className="details-card-section">
                      <div className="section-title">
                        <CreditCard size={18} />
                        <h4>Payment Details</h4>
                      </div>
                      <div className="section-body">
                        <div className="info-item">
                          <span className="label">Method</span>
                          <span className="value">{orderDetails.paymentMethod}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Status</span>
                          <span className={`status-tag ${orderDetails.paymentStatus?.toLowerCase()}`}>
                            {orderDetails.paymentStatus}
                          </span>
                        </div>
                      </div>
                      {orderDetails.transaction && (
                        <div className="section-body transaction-details">
                          <div className="transaction-header">Transaction Details</div>
                          <div className="info-item">
                            <span className="label">Transaction ID</span>
                            <span className="value transaction-id">{orderDetails.transaction.transactionNo}</span>
                          </div>
                          <div className="info-item">
                            <span className="label">Amount</span>
                            <span className="value">₹{orderDetails.transaction.amount}</span>
                          </div>
                          <div className="info-item">
                            <span className="label">Payment Method</span>
                            <span className="value">{orderDetails.transaction.paymentMethod}</span>
                          </div>
                          <div className="info-item">
                            <span className="label">Date</span>
                            <span className="value">{orderDetails.transaction.date}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items Section using Standard UI */}
                  <div className="order-details-grid">
                    <div className="details-card-section">
                      <div className="section-title">
                        <Package size={18} />
                        <h4>Order Items ({orderDetails.items.length})</h4>
                      </div>
                      <div className="section-body">
                        {orderDetails.items.map((item, idx) => {
                          // Weight-based items: prefer deriving "0.5 kg (250g
                          // × 2)" from the raw per-unit weight (item.weight,
                          // sent alongside weight_info at order creation)
                          // when the backend echoes it back. It doesn't for
                          // every order, so fall back to reformatting the
                          // backend's own weight_info string ("0.5 kg (0.25 ×
                          // 2)") by converting just the inner decimal-kg
                          // value into a g/kg label — keeping the same total
                          // weight prefix and × count, only making the
                          // per-unit number readable.
                          const unitWeightLabel = item.weight ? formatWeightLabel(item.weight) : null;
                          const weightCount = unitWeightLabel && item.weight > 0
                            ? Math.round(item.quantity / item.weight)
                            : null;

                          let simpleWeightLabel = null;
                          if (unitWeightLabel && weightCount) {
                            simpleWeightLabel = `${item.quantity} kg (${unitWeightLabel} × ${weightCount})`;
                          } else if (item.weightInfo) {
                            const match = item.weightInfo.match(/^([\d.]+)\s*kg\s*\(([\d.]+)\s*[×x]\s*(\d+)\)$/);
                            if (match) {
                              const [, totalKg, perUnitKg, count] = match;
                              const perUnitLabel = formatWeightLabel(Number(perUnitKg));
                              simpleWeightLabel = perUnitLabel ? `${totalKg} kg (${perUnitLabel} × ${count})` : item.weightInfo;
                            } else {
                              simpleWeightLabel = item.weightInfo; // e.g. single-portion "250 g" — already clear
                            }
                          }

                          return (
                          <div key={idx} className="order-item-row">
                            <span className="order-item-name">
                              {item.name}{' '}
                              {item.spiceLevel > 0 && (
                                <span style={{ marginRight: 4 }}><SpiceLevel level={item.spiceLevel} size={11} /></span>
                              )}
                              {!simpleWeightLabel && (
                                <span style={{fontSize: '12px', color: '#718096', fontWeight: '600', marginLeft: '4px'}}>x{item.quantity}</span>
                              )}
                            </span>
                            <span className="order-item-price">{item.total}</span>
                            {(item.variations?.length > 0 || item.extras?.length > 0 || item.comboGroups?.length > 0 || item.comboComponents?.length > 0 || item.addons?.length > 0 || simpleWeightLabel || item.instruction) && (
                              <div className="order-item-subinfo">
                                {simpleWeightLabel && (
                                  <div className="order-item-subinfo-line">{simpleWeightLabel}</div>
                                )}
                                {item.variations?.length > 0 && (
                                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                    {item.variations.map((v, i) => (
                                      <span key={i} style={{fontSize: '12px', padding: '2px 8px', background: '#f1f5f9', color: '#475569', borderRadius: '4px', fontWeight: '500'}}>
                                        {v.variation_name ? `${v.variation_name}: ${v.name || v}` : (v.name || v.variation_name || v)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {item.extras?.length > 0 && (
                                  <div className="order-item-subinfo-line">
                                    Modifiers: {item.extras.map(e => e.name || e.extra_name || e).join(', ')}
                                  </div>
                                )}
                                {/* Bundle/combo included items — same data the cart already renders */}
                                {item.comboGroups?.length > 0 && item.comboGroups.map((group, gi) => (
                                  <div key={gi} className="order-item-subinfo-line">
                                    <strong>{group.group_name}:</strong> {group.selections?.map((sel, si) => (
                                      <span key={si}>
                                        {sel.item_name}{sel.variation_name ? ` (${sel.variation_name})` : ''}{sel.extra_price > 0 ? ` +${sel.extra_price}` : ''}{si + 1 < group.selections.length ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                                {item.comboComponents?.length > 0 && (
                                  <div className="order-item-subinfo-line">
                                    {item.comboComponents.map((comp, ci) => (
                                      <span key={ci}>
                                        ×{comp.quantity || 1} {comp.display_label || comp.component_item_name}{comp.component_variation_name ? ` (${comp.component_variation_name})` : ''}{ci + 1 < item.comboComponents.length ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {item.addons?.length > 0 && (
                                  <div className="order-item-subinfo-line">
                                    Add-ons: {item.addons.map(a => a.addon_item_name || a.name || a).join(', ')}
                                  </div>
                                )}
                                {item.instruction && (
                                  <div className="order-item-subinfo-line" style={{color: '#dc3545'}}>Note: {item.instruction}</div>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Pricing Breakdown using Standard UI */}
                    <div className="details-card-section">
                      <div className="section-title">
                        <Receipt size={18} />
                        <h4>Payment Summary</h4>
                      </div>
                      <div className="section-body">
                        <div className="info-item">
                          <span className="label">Subtotal</span>
                          <span className="value">{orderDetails.subtotal}</span>
                        </div>
                        {parseFloat(orderDetails.discount?.replace(/[^0-9.-]+/g, "")) > 0 && (
                          <div className="info-item">
                            <span className="label">Discount</span>
                            <span className="value" style={{color: '#38a169'}}>-{orderDetails.discount}</span>
                          </div>
                        )}
                        <div className="info-item">
                          <span className="label">Tax</span>
                          <span className="value">{orderDetails.tax}</span>
                        </div>
                        {orderDetails.taxBreakdown && orderDetails.taxBreakdown.length > 0 && (
                          <div className="tax-breakdown-container" style={{ 
                            paddingLeft: '12px', 
                            marginLeft: '4px',
                            borderLeft: '2px solid #edf2f7',
                            marginBottom: '12px',
                            marginTop: '0'
                          }}>
                            <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>Tax Breakdown:</div>
                            {orderDetails.taxBreakdown.map((tax, idx) => (
                              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#718096', padding: '2px 0' }}>
                                <span>{tax.tax_name} ({tax.formatted_rate})</span>
                                <span>{tax.tax_amount_formatted}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {parseFloat(orderDetails.deliveryCharge?.replace(/[^0-9.-]+/g, "")) > 0 && (
                          <div className="info-item">
                            <span className="label">Delivery Fee</span>
                            <span className="value">{orderDetails.deliveryCharge}</span>
                          </div>
                        )}
                        {parseFloat(orderDetails.packagingCharge?.replace(/[^0-9.-]+/g, "")) > 0 && (
                          <div className="info-item">
                            <span className="label">Packaging Charge</span>
                            <span className="value">{orderDetails.packagingCharge}</span>
                          </div>
                        )}
                        {orderDetails.loyalty && parseFloat(orderDetails.loyalty.points_used) > 0 && (
                          <div className="info-item">
                            <span className="label">Loyalty Points Redeemed</span>
                            <span className="value" style={{color: '#38a169'}}>-{orderDetails.loyalty.loyalty_discount_formatted}</span>
                          </div>
                        )}
                        <div className="info-item" style={{borderTop: '2px solid #edf2f7', paddingTop: '16px', marginTop: '4px'}}>
                          <span className="label" style={{color: '#1a202c', fontWeight: '700'}}>Total Amount</span>
                          <span className="value" style={{fontSize: '22px', color: 'var(--color-primary, #ff6b35)'}}>{orderDetails.total}</span>
                        </div>
                        {orderDetails.loyalty && parseFloat(orderDetails.loyalty.points_earned) > 0 && (
                          <div className="info-item" style={{borderTop: 'none', paddingTop: '0'}}>
                            <span className="label" style={{fontSize: '12px'}}>Points earned</span>
                            <span className="value" style={{fontSize: '13px'}}>{orderDetails.loyalty.points_earned} pts</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cancel-order-section">
                    {cancelMessage && (
                      <div className={`cancel-order-message ${cancelMessage.type}`}>
                        {cancelMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                        <span>{cancelMessage.text}</span>
                      </div>
                    )}

                    {orderDetails && !([10, 13, 16, 19].includes(orderDetails.statusCode)) && (
                      <>
                        {!showCancelConfirm ? (
                          <button
                            className="cancel-order-btn"
                            onClick={() => { setShowCancelConfirm(true); setCancelMessage(null); }}
                          >
                            <XCircle size={16} />
                            <span>Cancel Order</span>
                          </button>
                        ) : (
                          <div className="cancel-order-confirm">
                            <p className="cancel-confirm-text">Are you sure you want to cancel this order?</p>
                            <input
                              type="text"
                              placeholder="Reason (optional)"
                              value={cancelReason}
                              onChange={(e) => setCancelReason(e.target.value)}
                              className="cancel-reason-input"
                              disabled={cancelLoading}
                            />
                            <div className="cancel-confirm-actions">
                              <button
                                className="cancel-confirm-no"
                                onClick={() => { setShowCancelConfirm(false); setCancelReason(''); }}
                                disabled={cancelLoading}
                              >
                                No, Keep Order
                              </button>
                              <button
                                className="cancel-confirm-yes"
                                disabled={cancelLoading}
                                onClick={async () => {
                                  const token = getValidAuthSession()?.token;
                                  if (!token) { setCancelMessage({ type: 'error', text: 'Please login again.' }); return; }
                                  setCancelLoading(true);
                                  setCancelMessage(null);
                                  try {
                                    const res = await axios.post(
                                      `${API_BASE_URL}/web/order/${orderDetails.orderId}/cancel`,
                                      { reason: cancelReason || 'Cancelled by customer' },
                                      {
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'X-Api-Key': X_API_KEY,
                                          'X-Tenant': getTenantId(),
                                          'Content-Type': 'application/json',
                                          'Accept': 'application/json'
                                        }
                                      }
                                    );
                                    setCancelMessage({ type: 'success', text: res.data.message || 'Order cancelled successfully.' });
                                    setShowCancelConfirm(false);
                                    setCancelReason('');

                                    // 1. Refresh order details to reflect cancelled status in current view
                                    if (selectedOrder) {
                                      setTimeout(() => fetchOrderDetails(selectedOrder), 1500);
                                    }

                                    // 2. Refresh the overall orders list to ensure list view is in sync
                                    fetchOrders(page);

                                  } catch (err) {
                                    const errMsg = err.response?.data?.message || 'Failed to cancel order.';
                                    setCancelMessage({ type: 'error', text: errMsg });
                                  } finally {
                                    setCancelLoading(false);
                                  }
                                }}
                              >
                                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};